// "Contracts & agreements" on a building: every contract that applies (its own + shared ones), by kind; each opens to
// its terms, documents and — when claims run against it — its claims.
import { CopyText } from '../CopyText';
import { pctText, toVip, vatOn } from '../../lib/vat';
import { fileTag } from '../../lib/fileType';
import type { Taggable } from '../../lib/fileTags';
import { FileNote, FileStatus, TagEditor } from '../FileTags';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router';
import { supabase } from '../../lib/supabase';
import { write } from '../../lib/api';
import { uploadFile } from '../../lib/files';
import { money } from '../../lib/claimCalc';
import type { Assessment, ToDate } from '../../lib/claimCalc';
import { fmtDate } from '../../lib/format';
import { statusLabel } from '../../lib/registers';
import { KINDS, coversText, docTypeLabel, kindOf, type Building, type Contract } from '../../lib/buildings';
import { useViewer } from '../Viewer';
import { Button, errText, useToast } from '../kit';
import { C3Busy } from '../C3Loader';

type Org = { id: string; name: string };
type Doc = { id: string; original_name: string; mime: string | null; kind: string; doc_type: string | null; linked_id: string; created_at: string; uploaded_at: string | null } & Taggable;
type ClaimRow = { item_id: string; contract_id: string; claim_no: number | null; period_to: string | null; pbs_total_vip: number | null; certified_at: string | null; locked_at: string | null; paid_on: string | null; assessment: { swi?: Assessment } | null; items: { number: string | null; status: string } | null };

export type ContractProgress = { toDate: ToDate; certifiedPct: number; lastCertified: number | null; claims: number };

/** Running totals for a contract: the last certified claim's snapshot, else the opening totals. */
export function progressOf(c: Contract, claims: ClaimRow[], approvedVar: number): ContractProgress {
  const mine = claims.filter((x) => x.contract_id === c.id);
  const certified = mine.filter((x) => x.locked_at && x.assessment?.swi?.toDate).sort((a, b) => (b.claim_no ?? 0) - (a.claim_no ?? 0));
  const toDate: ToDate = certified[0]?.assessment?.swi?.toDate ?? {
    gross_vep: Number(c.open_gross_vep ?? 0),
    variations_vep: Number(c.open_variations_vep ?? 0),
    deposit_recovered: Number(c.open_deposit_recovered ?? 0),
    retention_held: Number(c.open_retention_held ?? 0),
    paid_vep: Number(c.open_paid_vep ?? 0),
  };
  const value = Number(c.sum_vep ?? 0) + approvedVar;
  return { toDate, certifiedPct: value > 0 ? Math.min(100, (toDate.gross_vep / value) * 100) : 0, lastCertified: certified[0]?.claim_no ?? c.open_after_claim ?? null, claims: mine.length };
}

export function useContractClaims(contractIds: string[]) {
  const [rows, setRows] = useState<ClaimRow[]>([]);
  const key = contractIds.join(',');
  useEffect(() => {
    if (!contractIds.length) return setRows([]);
    void supabase
      .from('claim_details')
      .select('item_id, contract_id, claim_no, period_to, pbs_total_vip, certified_at, locked_at, paid_on, assessment, items(number, status)')
      .in('contract_id', contractIds)
      .is('deleted_at', null)
      .order('claim_no', { ascending: false })
      .then(({ data }) => setRows((data ?? []) as unknown as ClaimRow[]));
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  return rows;
}

export function ContractsCard({
  building,
  contracts,
  claims,
  approvedVar,
  orgs,
  names,
  siteId,
  canEdit,
  onEdit,
  onAdd,
  onRead,
}: {
  building: Building;
  contracts: Contract[];
  claims: ClaimRow[];
  approvedVar: number;
  orgs: Org[];
  names: Map<string, string>;
  siteId: string;
  canEdit: boolean;
  onEdit: (c: Contract) => void;
  onAdd: () => void;
  onRead: () => void;
}) {
  const toast = useToast();
  const viewer = useViewer();
  const [kind, setKind] = useState<string>('all');
  const [open, setOpen] = useState<string | null>(null);
  const [docs, setDocs] = useState<Doc[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const fileIn = useRef<HTMLInputElement>(null);
  const upFor = useRef<string | null>(null);
  const orgName = (id: string | null) => (id ? orgs.find((o) => o.id === id)?.name ?? '—' : null);
  const [tagging, setTagging] = useState<string | null>(null);

  const ids = contracts.map((c) => c.id).join(',');
  const loadDocs = async () => {
    if (!contracts.length) return setDocs([]);
    const { data } = await supabase
      .from('files')
      .select('id, original_name, mime, kind, doc_type, linked_id, created_at, uploaded_at, status_tag, review_flag, superseded_by, notes, note_by_name, note_at')
      .eq('linked_table', 'contracts')
      .in('linked_id', contracts.map((c) => c.id))
      .is('deleted_at', null)
      .eq('is_current', true)
      .order('created_at');
    setDocs((data ?? []) as Doc[]);
  };
  useEffect(() => void loadDocs(), [ids]); // eslint-disable-line react-hooks/exhaustive-deps

  // the building's own claims contract opens first
  useEffect(() => {
    if (open === null && contracts.length) setOpen((contracts.find((c) => c.has_claims && c.building_ids.includes(building.id)) ?? contracts[0]).id);
  }, [ids]); // eslint-disable-line react-hooks/exhaustive-deps

  const order = (c: Contract) => KINDS.findIndex((k) => k.value === c.kind);
  const sorted = useMemo(() => [...contracts].sort((a, b) => order(a) - order(b) || (a.title ?? '').localeCompare(b.title ?? '')), [contracts]);
  const shown = kind === 'all' ? sorted : sorted.filter((c) => c.kind === kind);

  async function addDoc(files: FileList | null) {
    const c = contracts.find((x) => x.id === upFor.current);
    if (!files?.length || !c) return;
    setBusy(c.id);
    try {
      for (const f of Array.from(files)) await uploadFile(f, { siteId, linkedTable: 'contracts', linkedId: c.id });
      await loadDocs();
      toast({ tone: 'ok', text: `${files.length} file${files.length === 1 ? '' : 's'} added to ${c.title ?? 'the contract'}` });
    } catch (e) {
      toast({ tone: 'error', text: errText(e) });
    } finally {
      setBusy(null);
      if (fileIn.current) fileIn.current.value = '';
    }
  }
  async function removeDoc(d: Doc) {
    if (!window.confirm(`Remove ${d.original_name} from this contract? (It can be restored from the history.)`)) return;
    try {
      await write('files', 'delete', { id: d.id, summary: `Removed ${d.original_name} from a contract` });
      await loadDocs();
    } catch (e) {
      toast({ tone: 'error', text: errText(e) });
    }
  }

  return (
    <section className="card bcon">
      <div className="card-head">
        <h2>Contracts &amp; agreements · {contracts.length}</h2>
        {canEdit ? (
          <span className="resp-row">
            <button type="button" className="linkbtn" onClick={onRead}>
              Upload a file — SWI AI reads it
            </button>
            <button type="button" className="linkbtn" onClick={onAdd}>
              + Add contract
            </button>
          </span>
        ) : null}
      </div>
      <p className="bcon-note">
        Everything that applies to {building.name}, including contracts shared with other buildings — so the close-out download is complete. Each contract is known by the <b>client ref (budget no.)</b> plus the <b>contractor's ref</b>.
      </p>
      {contracts.length > 1 ? (
        <div className="chips bcon-kinds" role="group" aria-label="Kind of contract">
          {[{ value: 'all', short: 'All' }, ...KINDS.map((k) => ({ value: k.value, short: k.value === 'main' ? 'Main contract' : k.value === 'building' ? 'Building contract' : k.short }))].map((k) => {
            const n = k.value === 'all' ? contracts.length : contracts.filter((c) => c.kind === k.value).length;
            if (!n && k.value !== 'all') return null;
            return (
              <button key={k.value} type="button" className={`chip ${kind === k.value ? 'on' : ''}`} aria-pressed={kind === k.value} onClick={() => setKind(k.value)}>
                {k.short} <span className="muted">{n}</span>
              </button>
            );
          })}
        </div>
      ) : null}
      {contracts.length === 0 ? (
        <div className="bcon-empty">
          No contract yet. {canEdit ? 'Upload the contract and let SWI AI fill it in, or add one by hand.' : ''}
        </div>
      ) : null}
      {shown.map((c) => {
        const k = kindOf(c.kind);
        const isOpen = open === c.id;
        const p = c.has_claims ? progressOf(c, claims, approvedVar) : null;
        const mine = claims.filter((x) => x.contract_id === c.id);
        const shared = c.covers_all || c.building_ids.length > 1;
        const cDocs = docs.filter((d) => d.linked_id === c.id);
        return (
          <div key={c.id} className="bcon-row">
            <button type="button" className="bcon-head" aria-expanded={isOpen} onClick={() => setOpen(isOpen ? '' : c.id)}>
              <svg className={`bchev ${isOpen ? 'open' : ''}`} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
                <path d="M9 6l6 6-6 6" />
              </svg>
              <span className="kind-tag" style={{ background: k.color }}>
                {k.short}
              </span>
              <span className="bcon-main">
                <span className="bcon-title">
                  <b>{c.title ?? 'Contract'}</b>
                  {orgName(c.contractor_org_id) ? <span className="bpill">{orgName(c.contractor_org_id)}</span> : null}
                  {shared ? <span className="bpill shared">{coversText(c, names)}</span> : null}
                </span>
                <span className="muted small">
                  {cDocs.length} document{cDocs.length === 1 ? '' : 's'}
                  {c.has_claims ? ` · VAT ${pctText(vatOn(c))}` : ''}
                </span>
              </span>
              {p ? (
                <span className="bcon-prog">
                  <span className="track" role="img" aria-label={`${Math.round(p.certifiedPct)} % certified`}>
                    <i style={{ width: `${p.certifiedPct}%` }} />
                  </span>
                  <span className="muted small">{Math.round(p.certifiedPct)} % certified</span>
                </span>
              ) : null}
              <span className="bcon-sum">
                <b>{c.sum_vep ? `${money(c.sum_vep)} VEP` : '—'}</b>
                {c.sum_vep ? <span className="muted small">{money(toVip(Number(c.sum_vep), vatOn(c, c.signed_on)))} VIP</span> : null}
              </span>
            </button>
            <div className="bcon-refs">
              <span>Client ref</span>
              <CopyText value={c.contract_no} label="client ref" />
              <span>Contractor's ref</span>
              <CopyText value={c.project_no} label="contractor's ref" />
            </div>
            {isOpen ? (
              <div className="bcon-body">
                {c.has_claims ? (
                  <dl className="terms">
                    <div>
                      <dt>Contract sum</dt>
                      <dd>
                        <b>{c.sum_vep ? `${money(c.sum_vep)} VEP` : '—'}</b>
                        <span>
                          {c.sum_vep
                            ? `${money(toVip(Number(c.sum_vep), vatOn(c, c.signed_on)))} VIP at ${pctText(vatOn(c, c.signed_on))}${vatOn(c) !== vatOn(c, c.signed_on) ? ` · ${money(toVip(Number(c.sum_vep), vatOn(c)))} at ${pctText(vatOn(c))} now` : ''}`
                            : 'to enter'}
                        </span>
                      </dd>
                    </div>
                    <div>
                      <dt>Deposit</dt>
                      <dd>
                        <b>{c.deposit_pct !== null ? `${c.deposit_pct} %${c.sum_vep ? ` · ${money((c.sum_vep * c.deposit_pct) / 100)}` : ''}` : '—'}</b>
                        <span>{c.deposit_recovery_pct !== null ? `recovered ${c.deposit_recovery_pct} % of deposit per claim` : 'recovery % to enter'}</span>
                      </dd>
                    </div>
                    <div>
                      <dt>Retention</dt>
                      <dd>
                        <b>{c.retention_pct !== null ? `${c.retention_pct} % per claim` : '—'}</b>
                        <span>{c.retention_limit_pct !== null ? `limit ${c.retention_limit_pct} % of contract + approved var.` : 'limit to enter'}</span>
                      </dd>
                    </div>
                    <div>
                      <dt>Defects liability</dt>
                      <dd>
                        <b>{c.dlp_months ? `${c.dlp_months} months` : '—'}</b>
                        <span>{c.signed_on ? `signed ${fmtDate(c.signed_on)}` : ''}</span>
                      </dd>
                    </div>
                    <div>
                      <dt>Contractor</dt>
                      <dd>
                        <b>{orgName(c.contractor_org_id) ?? '—'}</b>
                      </dd>
                    </div>
                    <div>
                      <dt>Employer</dt>
                      <dd>
                        <b>{c.employer_name ?? '—'}</b>
                        <span>on the certificate</span>
                      </dd>
                    </div>
                    <div>
                      <dt>Architect of record</dt>
                      <dd>
                        <b>{orgName(c.architect_org_id) ?? '—'}</b>
                        <span>signs the payment certificates</span>
                      </dd>
                    </div>
                    <div>
                      <dt>Running totals</dt>
                      <dd>
                        <b>{p?.lastCertified ? `after claim ${p.lastCertified}` : 'none yet'}</b>
                        <span>retention held {money(p?.toDate.retention_held ?? 0)}</span>
                      </dd>
                    </div>
                  </dl>
                ) : (
                  <p className="muted small" style={{ margin: 0 }}>
                    {k.label} · {coversText(c, names, 6)}. Kept for the record — no claims run against it.
                  </p>
                )}
                <div className="bcon-docs">
                  <div className="resp-row">
                    <span className="k grow">Documents in this contract</span>
                    {canEdit ? (
                      <>
                        <Button
                          disabled={busy === c.id}
                          onClick={() => {
                            upFor.current = c.id;
                            fileIn.current?.click();
                          }}
                        >
                          {busy === c.id ? <C3Busy text="Uploading…" /> : '+ Upload'}
                        </Button>
                        <Button onClick={() => onEdit(c)}>Edit contract</Button>
                      </>
                    ) : null}
                  </div>
                  {cDocs.length === 0 ? <p className="muted small">No documents yet.</p> : null}
                  {cDocs.map((d) => (
                    <div key={d.id} className="doc-wrap">
                    <div className="doc-row">
                      <span className={`ext ${fileTag(d.original_name, d.kind).cls}`}>{fileTag(d.original_name, d.kind).label}</span>
                      <button type="button" className="linkbtn grow" onClick={() => viewer({ id: d.id, name: d.original_name, mime: d.mime, kind: d.kind }, cDocs.map((x) => ({ id: x.id, name: x.original_name, mime: x.mime, kind: x.kind })))}>
                        {d.original_name}
                      </button>
                      <FileStatus f={d} others={docs} />
                      <span className="bpill">{docTypeLabel(d.doc_type)}</span>
                      <span className="muted small">{fmtDate(d.uploaded_at ?? d.created_at)}</span>
                      {canEdit ? (
                        <button type="button" className="linkbtn small strong" aria-label={`Tag and note for ${d.original_name}`} onClick={() => setTagging(tagging === d.id ? null : d.id)}>
                          Tag
                        </button>
                      ) : null}
                      {canEdit ? (
                        <button type="button" className="linkbtn danger small" aria-label={`Remove ${d.original_name}`} onClick={() => void removeDoc(d)}>
                          ✕
                        </button>
                      ) : null}
                    </div>
                    <FileNote f={d} />
                    {tagging === d.id ? (
                      <TagEditor
                        f={d}
                        others={cDocs}
                        onCancel={() => setTagging(null)}
                        onDone={() => {
                          setTagging(null);
                          void loadDocs();
                        }}
                      />
                    ) : null}
                    </div>
                  ))}
                </div>
                {c.has_claims ? (
                  <div>
                    <div className="k" style={{ marginBottom: 6 }}>
                      Claims on this contract
                    </div>
                    {mine.length === 0 ? (
                      <p className="muted small">No claims linked yet — a claim links to this contract when it's set up.</p>
                    ) : (
                      <div className="table-wrap">
                        <table className="t">
                          <thead>
                            <tr>
                              <th>Claim</th>
                              <th>Period to</th>
                              <th className="right">PBS claimed · VIP</th>
                              <th className="right">Certified · VIP</th>
                              <th>Status</th>
                              <th>Paid</th>
                            </tr>
                          </thead>
                          <tbody>
                            {mine.slice(0, 6).map((r) => (
                              <tr key={r.item_id}>
                                <td>
                                  <Link to={`/r/claims/${r.item_id}`}>Claim {r.claim_no ?? r.items?.number ?? ''}</Link>
                                </td>
                                <td>{fmtDate(r.period_to)}</td>
                                <td className="right">{r.pbs_total_vip !== null ? money(r.pbs_total_vip) : '—'}</td>
                                <td className="right">{r.locked_at && r.assessment?.swi ? money(r.assessment.swi.payableVip) : '—'}</td>
                                <td>{r.items ? statusLabel(r.items.status, 'claim') : ''}</td>
                                <td>{r.paid_on ? fmtDate(r.paid_on) : '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                    <Link className="small strong" to={`/r/claims?b=${building.id}`}>
                      All claims for {building.name} →
                    </Link>
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        );
      })}
      <input ref={fileIn} type="file" multiple hidden onChange={(e) => void addDoc(e.target.files)} />
    </section>
  );
}
